import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import {
  CATEGORIES as FALLBACK_CATEGORIES,
  CITIES,
  SEGMENTS,
  SLOTS,
  FAQS,
  TESTIMONIALS,
  TRUST_BADGES,
  CONTACT,
  AMC_PLANS,
} from "../data/catalog";

const CatalogContext = createContext();

const orderCategories = (items) => {
  const roCategory = items.find((item) => item.id === "ro");
  return roCategory ? [roCategory, ...items.filter((item) => item.id !== "ro")] : items;
};

export const CatalogProvider = ({ children }) => {
  const [categories, setCategories] = useState(orderCategories(FALLBACK_CATEGORIES));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadCatalog = async () => {
      if (mounted) setLoading(true);
      try {
        const response = await axios.get(`${API}/catalog`);
        const fetched = response.data?.categories;
        if (!Array.isArray(fetched) || fetched.length === 0) throw new Error("Catalog unavailable");
        if (!mounted) return;
        if (Array.isArray(fetched) && fetched.length > 0) {
          const mapped = fetched.map((category) => {
            const fallback = FALLBACK_CATEGORIES.find((item) => item.id === category.id) || {};
            return {
              ...fallback,
              ...category,
              name: category.name || category.display || fallback.name,
              display: category.display || fallback.display || category.name || fallback.name,
              short: category.short || fallback.short || category.name || category.id,
              tagline: category.tagline || fallback.tagline || "",
              startingPrice: category.startingPrice ?? fallback.startingPrice ?? 0,
              brands: category.brands || fallback.brands || [],
              color: category.color || fallback.color || "#1A3C6E",
              bg: category.bg || fallback.bg || "#EDE9FE",
              icon: fallback.icon,
              services: Array.isArray(category.services) ? category.services : fallback.services || [],
            };
          });
          setCategories(mapped);
          setError(null);
        }
      } catch (err) {
        console.warn("Unable to load catalog from /api/catalog", err);
        setError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadCatalog();
    window.addEventListener("catalog-updated", loadCatalog);
    window.addEventListener("focus", loadCatalog);
    return () => {
      window.removeEventListener("catalog-updated", loadCatalog);
      window.removeEventListener("focus", loadCatalog);
      mounted = false;
    };
  }, []);

  const categoriesById = useMemo(
    () => categories.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {}),
    [categories],
  );

  const getCategoryById = (id) => categoriesById[id] || null;
  const getServiceById = (categoryId, serviceId) => {
    const category = categoriesById[categoryId];
    return category?.services?.find((service) => service.id === serviceId) || null;
  };

  return (
    <CatalogContext.Provider
      value={{
        categories,
        loading,
        error,
        getCategoryById,
        getServiceById,
        CITIES,
        SEGMENTS,
        SLOTS,
        FAQS,
        TESTIMONIALS,
        TRUST_BADGES,
        CONTACT,
        AMC_PLANS,
      }}
    >
      {children}
    </CatalogContext.Provider>
  );
};

export const useCatalog = () => {
  const context = useContext(CatalogContext);
  if (!context) {
    throw new Error("useCatalog must be used within CatalogProvider");
  }
  return context;
};
